/**
 * Where a signature goes on each form.
 *
 * Two kinds of anchor, because the forms come in two kinds:
 *
 *   byField — the PDF has a named AcroForm field for the signature. The
 *             rectangle is read from the PDF itself, so the mark lands
 *             correctly even if the state reissues the form with the box
 *             moved. Preferred wherever it exists.
 *
 *   byRect  — the form has a ruled line with no field behind it, which is the
 *             case on the 82993 and the 83330. The coordinates were measured
 *             off the rendered page. These are the fragile ones: if FLHSMV
 *             revises the form, they must be re-measured. Each carries a note
 *             saying what it was measured against.
 *
 * PDF coordinates start at the bottom-left of the page, which is why the
 * numbers look upside down compared to a screen.
 */

export type SignerRole = "buyer" | "co_buyer" | "dealer";

export interface Anchor {
  /** Which signer's mark belongs here. */
  role: SignerRole;
  /** 0-based page within that document. */
  page: number;
  /** Named AcroForm field to sit on top of. */
  field?: string;
  /** Fallback when there is no field: [x0, y0, x1, y1] in PDF points. */
  rect?: [number, number, number, number];
  /** Initials rather than a full signature. */
  initials?: boolean;
  /** Printed under the line — some forms want the name typed as well. */
  alsoPrintName?: string;
  /** For the reader of this file, and for whoever re-measures it later. */
  note?: string;
}

/**
 * Keyed by the same document keys the packet builder uses.
 *
 * The Buyers Guide is deliberately absent. The FTC revised it in 2018 and the
 * current form carries no signature line at all — the acknowledgment lives on
 * the purchase agreement instead. Adding a signature where the form does not
 * ask for one would be inventing paperwork.
 */
export const ANCHORS: Record<string, Anchor[]> = {
  /* ---- Application for certificate of title -------------------------- */
  hsmv_82040: [
    { role: "buyer", page: 1, field: "Signature of Applicant, Owner" },
    { role: "co_buyer", page: 1, field: "Signature of Applicant, Co-Owner" },
    { role: "dealer", page: 1, field: "DealerAgent Signature" },
  ],

  /* ---- Separate odometer disclosure -----------------------------------
     No fields behind the signature cells. Both sit immediately left of the
     printed-name field on the same row, running from the form's left margin
     (x=46.4, shared with the street-address field below) to just short of the
     printed-name box at x=320.

     The dealership is the SELLER here, which is why the seller line takes the
     dealer signature and not the buyer's. Getting these two the wrong way
     round produces a form that looks right and says the opposite. */
  hsmv_82993: [
    {
      role: "dealer", page: 0, rect: [50, 428, 312, 447],
      note: "Seller's Signature cell, below its caption. Caption baseline sits at y 450; cell floor at y 428.",
    },
    {
      role: "buyer", page: 0, rect: [50, 326, 312, 345],
      note: "Buyer's Signature cell, below its caption. Same geometry as the seller cell, 102 points lower.",
    },
  ],

  /* ---- Florida insurance affidavit ------------------------------------
     One ruled line labelled "Signature of Insured", measured from the
     rendered page at 100 dpi. The insured is the buyer. */
  hsmv_83330: [
    {
      role: "buyer", page: 0, rect: [312, 436, 545, 458],
      note: "Ruled line above the 'Signature of Insured' caption",
    },
  ],

  /* ---- Our own purchase agreement --------------------------------------
     Fields all the way, because we drew the form. The initials are the
     as-is acknowledgment and the arbitration notice: both are places the law
     wants separate assent rather than one signature covering everything. */
  purchase_agreement: [
    { role: "buyer", page: 0, field: "asis_buyer_initials", initials: true },
    { role: "co_buyer", page: 0, field: "asis_cobuyer_initials", initials: true },
    { role: "buyer", page: 1, field: "notice_buyer_initials", initials: true },
    { role: "co_buyer", page: 1, field: "notice_cobuyer_initials", initials: true },
    { role: "buyer", page: 1, field: "buyer_sig" },
    { role: "co_buyer", page: 1, field: "cobuyer_sig" },
    { role: "dealer", page: 1, field: "dealer_sig" },
  ],

  /* ---- Deposit receipt -------------------------------------------------- */
  deposit_receipt: [
    { role: "buyer", page: 0, field: "customer_sig" },
    { role: "co_buyer", page: 0, field: "cocustomer_sig" },
    { role: "dealer", page: 0, field: "dealer_sig" },
  ],
};

/** Documents that carry no signature and are included for the record only. */
export const UNSIGNED_DOCS = new Set([
  "buyers_guide_en",
  "buyers_guide_es",
  "buyers_guide_warranty",
  "warranty_page",
]);

export const anchorsFor = (docKey: string): Anchor[] => ANCHORS[docKey] ?? [];

/** Does anyone need to sign this document at all? */
export const needsSignature = (docKey: string) => anchorsFor(docKey).length > 0;

/**
 * Which people this packet needs a signature from. Drives the flow: a deal
 * with no co-buyer must not sit waiting for a second person who does not
 * exist.
 */
export function rolesNeeded(docKeys: string[], hasCoBuyer: boolean): SignerRole[] {
  const found = new Set<SignerRole>();
  for (const key of docKeys) {
    for (const a of anchorsFor(key)) found.add(a.role);
  }
  if (!hasCoBuyer) found.delete("co_buyer");
  return (["buyer", "co_buyer", "dealer"] as SignerRole[]).filter((r) => found.has(r));
}

export const ROLE_LABEL: Record<SignerRole, string> = {
  buyer: "Buyer",
  co_buyer: "Co-buyer",
  dealer: "Dealer",
};
